<template>
  <div ref="configuratorEl" class="configurator" @scroll="handleConfiguratorScroll">
    <!-- Minimal scroll hint (only shows when scrollable + at top, hides after first scroll) -->
    <transition name="scrollhint-fade">
      <div v-show="showScrollHint" class="scroll-hint" aria-hidden="true">
        <!-- Match the minimalist thick chevron style -->
        <svg class="scroll-hint-icon" viewBox="0 0 32 20" focusable="false">
          <!-- Slightly shallower chevron (wider angle) -->
          <path d="M3 3 L16 10 L29 3" />
        </svg>
      </div>
    </transition>

    <!-- Step 1: Enclosure Material Selection (disabled - black only) -->
    <!-- <div class="section">
      <EnclosureSelector
        :current-material="currentEnclosureMaterial"
        :is-loading="isLoading"
        @material-changed="handleEnclosureMaterialChange"
      />
    </div> -->

    <!-- Step 2: Knob Type Selection (disabled - Davies only) -->
    <!-- <div class="section">
      <KnobTypeSelector 
        :current-knob-type="currentKnobType"
        :is-loading="isLoading"
        @knob-type-changed="handleKnobTypeChange"
      />
    </div> -->

    <!-- Step 3: Knob Material Selection (appears after knob type is selected) -->
    <div class="section knob-color-section" v-if="currentKnobType">
      <h2 class="section-title">KNOB COLOR</h2>
      <div class="header-divider"></div>
      <MaterialSelector 
        :current-material="currentMaterial"
        :available-materials="availableMaterials"
        :selected-knob-type-name="selectedKnobTypeName"
        :is-loading="isLoading"
        @material-changed="handleMaterialChange"
      />
    </div>

    <!-- Step 4: LED Color Selection -->
    <div class="section">
      <LEDSelector 
        :current-l-e-d="currentLEDMaterial"
        :is-loading="isLoading"
        @led-changed="handleLEDChange"
      />
    </div>

    <!-- Step 5: AI Image Generator -->
    <!-- <div class="section">
      <ImageGenerator />
    </div> -->

    <!-- Step 6: Checkout Button -->
    <div class="section">
      <CheckoutButton
        :configuration-data="currentConfiguration"
        :is-loading="isLoading"
        product-id="custom-pedal-configurator"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType, onMounted, onUnmounted, nextTick } from 'vue';
import { KNOB_CONFIGURATION } from '@/config/knobConfig';
// import KnobTypeSelector from './KnobTypeSelector.vue';
import MaterialSelector from './MaterialSelector.vue';
// import EnclosureSelector from './EnclosureSelector.vue';
import LEDSelector from './LEDSelector.vue';
import CheckoutButton from './CheckoutButton.vue';
// import ImageGenerator from './ImageGenerator.vue';
import { ENCLOSURE_MATERIALS } from '@/config/enclosureConfig';
import { LED_MATERIALS } from '@/config/ledConfig';
import { ConfigurationData } from '@/utils/configurationHelper';

export default defineComponent({
  name: 'KnobConfigurator',
  components: { 
    // KnobTypeSelector,
    MaterialSelector,
    // EnclosureSelector,
    LEDSelector,
    CheckoutButton,
    // ImageGenerator
  },
  props: {
    currentConfiguration: {
      type: Object as PropType<ConfigurationData>,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['knobTypeChanged', 'materialChanged', 'enclosureMaterialChanged', 'ledChanged'],
  setup(props, { emit }) {
    const configuratorEl = ref<HTMLElement | null>(null);
    const isConfiguratorScrollable = ref(false);
    const hasDismissedScrollHint = ref(false);
    const currentScrollTop = ref(0);

    // Ensure we start with a valid enclosure material
    const firstEnclosureMaterial = ENCLOSURE_MATERIALS[0]?.id || 'brushed_silver';
    
    const currentKnobType = ref<string>('classic'); // Davies only
    const currentMaterial = ref<string>('classic_black'); // Start with first Davies material
    const currentEnclosureMaterial = ref<string>(firstEnclosureMaterial); // Start with first available enclosure material
    const currentLEDMaterial = ref<string>('led_red'); // Start with red LED

    const knobTypes = KNOB_CONFIGURATION;

    const selectedKnobType = computed(() => 
      knobTypes.find(k => k.id === currentKnobType.value)
    );

    const selectedKnobTypeName = computed(() => 
      selectedKnobType.value?.displayName || ''
    );

    const availableMaterials = computed(() => 
      selectedKnobType.value?.materials || []
    );

    const showScrollHint = computed(() => {
      // Don't show while the app-level loading overlay is up.
      if (props.isLoading) return false;
      if (hasDismissedScrollHint.value) return false;
      if (!isConfiguratorScrollable.value) return false;
      return currentScrollTop.value <= 1;
    });

    const recalcConfiguratorScrollable = () => {
      const el = configuratorEl.value;
      if (!el) return;
      // Small epsilon avoids flicker on fractional pixels.
      isConfiguratorScrollable.value = el.scrollHeight > (el.clientHeight + 4);
    };

    const handleConfiguratorScroll = () => {
      const el = configuratorEl.value;
      if (!el) return;
      currentScrollTop.value = el.scrollTop;
      // As soon as the user starts scrolling, fade it out and keep it hidden.
      if (el.scrollTop > 1) {
        hasDismissedScrollHint.value = true;
      }
    };

    const handleKnobTypeChange = (knobTypeId: string) => {
      currentKnobType.value = knobTypeId;
      
      // Emit knob type change first so the 3D scene switches knob meshes
      // before we try to apply the first material.
      emit('knobTypeChanged', knobTypeId);

      // Auto-select first material of the new knob type
      const firstMaterial = availableMaterials.value[0];
      if (firstMaterial) {
        currentMaterial.value = firstMaterial.id;
        emit('materialChanged', firstMaterial.id);
      }
    };

    const handleMaterialChange = (materialId: string) => {
      currentMaterial.value = materialId;
      emit('materialChanged', materialId);
    };

    const handleEnclosureMaterialChange = (materialId: string) => {
      currentEnclosureMaterial.value = materialId;
      emit('enclosureMaterialChanged', materialId);
    };

    const handleLEDChange = (ledId: string) => {
      currentLEDMaterial.value = ledId;
      emit('ledChanged', ledId);
    };

    // Emit initial state on mount
    setTimeout(() => {
      emit('knobTypeChanged', currentKnobType.value);
      emit('materialChanged', currentMaterial.value);
      emit('enclosureMaterialChanged', currentEnclosureMaterial.value);
      emit('ledChanged', currentLEDMaterial.value);
    }, 100);

    let resizeObserver: ResizeObserver | null = null;

    onMounted(async () => {
      await nextTick();
      recalcConfiguratorScrollable();

      // Recalculate if the panel size/content size changes.
      if (typeof ResizeObserver !== 'undefined' && configuratorEl.value) {
        resizeObserver = new ResizeObserver(() => {
          recalcConfiguratorScrollable();
        });
        resizeObserver.observe(configuratorEl.value);
      }

      // Also respond to viewport changes (mobile orientation, etc.)
      window.addEventListener('resize', recalcConfiguratorScrollable);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', recalcConfiguratorScrollable);
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    });

    return {
      configuratorEl,
      showScrollHint,
      handleConfiguratorScroll,
      currentKnobType,
      currentMaterial,
      currentEnclosureMaterial,
      currentLEDMaterial,
      knobTypes,
      selectedKnobType,
      selectedKnobTypeName,
      availableMaterials,
      handleKnobTypeChange,
      handleMaterialChange,
      handleEnclosureMaterialChange,
      handleLEDChange
    };
  }
});
</script>

<style scoped>
.configurator {
  padding: 1rem 1.5rem; 
  height: 100%;
  background-color: rgb(255, 255, 255);
  color: rgb(0, 0, 0);
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  /* Prevent scroll chaining to the parent page when reaching top/bottom */
  overscroll-behavior-y: contain;
  /* iOS: prevent left/right scroll/pan bounce */
  overscroll-behavior-x: none;
  touch-action: pan-y;
}

.scroll-hint {
  position: absolute;
  left: 50%;
  /* Place higher so it's visible immediately (and above iOS bottom bar). */
  bottom: calc(56px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  opacity: 0.7;
  pointer-events: none;
  z-index: 5;
  /* Slow the bobbing motion by ~40% */
  animation: scrollHintFloat 5.33s ease-in-out infinite;
}

.scrollhint-fade-enter-active,
.scrollhint-fade-leave-active {
  transition: opacity 320ms ease;
}

.scrollhint-fade-enter-from,
.scrollhint-fade-leave-to {
  opacity: 0;
}

.scroll-hint-icon {
  width: 44px;
  height: 44px;
  display: block;
}

.scroll-hint-icon path {
  fill: none;
  stroke: #000;
  stroke-width: 2.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

@keyframes scrollHintFloat {
  0% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(4px); }
  100% { transform: translateX(-50%) translateY(0); }
}

.section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 2.6833rem; /* 2.4394rem * 1.10 */
  margin: 0 0 0.2rem 0;
  font-weight: 400;
  letter-spacing: 0.000em;
}

.header-divider {
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 0.35);
  margin: 0.35rem 0 2.5515rem 0;
}

/* Custom scrollbar for webkit browsers */
.configurator::-webkit-scrollbar {
  width: 8px;
}

.configurator::-webkit-scrollbar-track {
  background: #1a1a1a;
  border-radius: 4px;
}

.configurator::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 4px;
}

.configurator::-webkit-scrollbar-thumb:hover {
  background: #777;
}

/* Mobile responsive */
@media screen and (max-width: 768px) {
  .configurator {
    padding: calc(0.8rem - 0.2in) 1rem 0.8rem 1rem;
  }

  .scroll-hint {
    bottom: calc(71px + env(safe-area-inset-bottom, 0px));
  }
  
  .section {
    margin-bottom: 1.2rem;
  }

  /* Knob Color section: match Enclosure mobile sizing/spacing */
  .knob-color-section .section-title {
    font-size: 2.3232rem;
  }

  .knob-color-section .header-divider {
    margin: 0.35rem 0 2.0412rem 0;
  }
}
</style>
